import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Popover,
    Button,
    IconButton,
    Grid,
    Paper
} from '@mui/material';
import {
    RefreshIcon,
    CalendarTodayOutlinedIcon,
    AddIcon,
    OpenInNewIcon,
    CloseIcon
} from 'icons/materialIcons';
import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from 'icons/materialIcons';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import PropTypes from 'prop-types';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { useNavigate } from 'react-router-dom';
import ExportModal from './ExportModal';
import ExportUtils from 'utils/contacts/ExportUtils';
import {
    formatDate,
    formatFullDate,
    formatMonthYear,
    isSameDay,
    isInRange,
    addMonths,
    generateCalendarDays
} from 'utils/dateUtils';

const ContactsMenu = ({
    totalContacts,
    onRefresh,
    onAddLeads,
    onExport,
    contacts = [],
    selectedCount = 0,
    onDateRangeChange
}) => {
    const [showUpgradeSnackbar, setShowUpgradeSnackbar] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const exportButtonRef = useRef(null);
    const dateButtonRef = useRef(null);
    const navigate = useNavigate();

    // Default date range - initially set to all time
    const [dateRange, setDateRange] = useState([
        new Date('2024-01-01'),
        new Date()
    ]);

    // Current calendar view - month being displayed
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());

    // State for date picker popover
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [tempDateRange, setTempDateRange] = useState(dateRange);
    const [currentStep, setCurrentStep] = useState(0); // 0 = start date, 1 = end date

    // Current calendar display
    const [calendarDays, setCalendarDays] = useState([]);

    // Notification states
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Update calendar when view date changes
    useEffect(() => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        setCalendarDays(generateCalendarDays(year, month));
    }, [calendarViewDate]);

    // Predefined date ranges
    const getPredefinedRanges = () => {
        const today = new Date();

        // Helper function to get date N days ago
        const daysAgo = (days) => {
            const date = new Date(today);
            date.setDate(date.getDate() - days);
            return date;
        };

        // Helper function to get first day of month
        const firstDayOfMonth = (monthsAgo = 0) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo);
            date.setDate(1);
            return date;
        };

        // Helper function to get last day of month
        const lastDayOfMonth = (monthsAgo = 1) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo + 1);
            date.setDate(0);
            return date;
        };

        return [
            { label: 'Today', range: [today, today] },
            { label: 'Yesterday', range: [daysAgo(1), daysAgo(1)] },
            { label: 'Last 7 days', range: [daysAgo(6), today] },
            { label: 'Last 30 days', range: [daysAgo(29), today] },
            { label: 'This month', range: [firstDayOfMonth(), today] },
            { label: 'Last month', range: [firstDayOfMonth(1), lastDayOfMonth(1)] },
            { label: 'All time', range: [new Date('2024-01-01'), today] }
        ];
    };

    const predefinedRanges = getPredefinedRanges();

    // Reset upgrade notification when opening export modal
    useEffect(() => {
        if (!isExportModalOpen) {
            setShowUpgradeSnackbar(false);
        }
    }, [isExportModalOpen]);

    // Handle notification close
    const handleNotificationClose = useCallback(() => {
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    // Show notification helper
    const showNotification = useCallback((message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    }, []);

    // Handle date range button click
    const handleDateRangeClick = (event) => {
        setDatePopoverOpen(true);
        dateButtonRef.current = event.currentTarget;
        setTempDateRange(dateRange);
        setCurrentStep(0);
        setCalendarViewDate(new Date()); // Reset calendar view to current month
    };

    // Handle date popover close
    const handleDatePopoverClose = () => {
        setDatePopoverOpen(false);
    };

    // Handle applying the selected date range
    const handleApplyDateRange = () => {
        // Make sure start date is always before or equal to end date
        let sortedRange = [...tempDateRange];
        if (tempDateRange[0] > tempDateRange[1]) {
            sortedRange = [tempDateRange[1], tempDateRange[0]];
        }

        // Adjust the end date to include the entire day (23:59:59)
        const adjustedEndDate = new Date(sortedRange[1]);
        adjustedEndDate.setHours(23, 59, 59, 999);

        const finalRange = [
            sortedRange[0],  // Start date stays at beginning of day
            adjustedEndDate  // End date set to end of day
        ];

        setDateRange(finalRange);
        setDatePopoverOpen(false);

        // Call the parent component's handler if provided
        if (onDateRangeChange) {
            onDateRangeChange(finalRange[0], finalRange[1]);
        }

        // Show success notification
        showNotification(`Date filter applied: ${formatFullDate(finalRange[0])} - ${formatFullDate(finalRange[1])}`, 'success');
    };

    // Reset date range to all time
    const handleResetDateRange = () => {
        const allTimeRange = predefinedRanges.find(r => r.label === 'All time').range;
        setTempDateRange(allTimeRange);
        setDateRange(allTimeRange);
        setDatePopoverOpen(false);

        // Call the parent component's handler if provided
        if (onDateRangeChange) {
            onDateRangeChange(allTimeRange[0], allTimeRange[1]);
        }

        // Show success notification
        showNotification('Date filter reset to all time', 'info');
    };

    // Handle selecting a predefined range
    const handleSelectPredefinedRange = (range) => {
        // Adjust end date to include the entire day
        const adjustedEndDate = new Date(range[1]);
        adjustedEndDate.setHours(23, 59, 59, 999);

        const adjustedRange = [range[0], adjustedEndDate];

        setTempDateRange(adjustedRange);
        setCurrentStep(1); // Move to end date after selecting start
    };

    // Handle date selection in the calendar
    const handleDateSelect = (date) => {
        if (!date) return;

        if (currentStep === 0) {
            // Selecting start date
            setTempDateRange([date, tempDateRange[1]]);
            setCurrentStep(1); // Move to end date after selecting start
        } else {
            // Selecting end date
            setTempDateRange([tempDateRange[0], date]);
            // Don't auto-apply, let user click Apply
        }
    };

    // Navigation functions for calendar
    const handlePrevMonth = () => {
        setCalendarViewDate(addMonths(calendarViewDate, -1));
    };

    const handleNextMonth = () => {
        setCalendarViewDate(addMonths(calendarViewDate, 1));
    };

    const handleClose = () => {
        setShowUpgradeSnackbar(false);
    };

    const handleSubscribe = () => {
        handleClose();
        navigate('/dashboard/subscribe');
    };

    const handleExportClick = useCallback((event) => {
        // Store the button reference for positioning the modal
        if (event && event.currentTarget) {
            exportButtonRef.current = event.currentTarget;
        }

        setIsExportModalOpen(true);
    }, [contacts]);

    const handleExportModalClose = useCallback(() => {
        setIsExportModalOpen(false);
    }, []);

    // Function to handle CSV export from the modal
    const handleExportToCSV = useCallback(() => {
        // Close the modal first
        setIsExportModalOpen(false);

        // Export all contacts to CSV
        if (contacts && Array.isArray(contacts) && contacts.length > 0) {
            try {
                const timestamp = new Date().toISOString().slice(0, 10);
                // Update the filename to indicate selected contacts if any
                const filename = selectedCount > 0
                    ? `Selected_Contacts_${selectedCount}_${timestamp}.csv`
                    : `All_Contacts_${timestamp}.csv`;
                ExportUtils.exportMultipleLeads(contacts, filename);

                // Show success notification
                showNotification(
                    selectedCount > 0
                        ? `Successfully exported ${selectedCount} contacts to CSV`
                        : `Successfully exported ${contacts.length} contacts to CSV`,
                    'success'
                );
            } catch (error) {
                // Show error notification
                showNotification('Failed to export contacts to CSV. Please try again.', 'error');
            }
        } else {
            // Show warning notification
            showNotification('No contacts available to export', 'warning');
        }
    }, [contacts, selectedCount, showNotification]);

    // Function to handle Excel export from the modal
    const handleExportToExcel = useCallback(() => {
        // Close the modal first
        setIsExportModalOpen(false);

        // Export all contacts to Excel
        if (contacts && Array.isArray(contacts) && contacts.length > 0) {
            try {
                const timestamp = new Date().toISOString().slice(0, 10);
                // Update the filename to indicate selected contacts if any
                const filename = selectedCount > 0
                    ? `Selected_Contacts_${selectedCount}_${timestamp}.xlsx`
                    : `All_Contacts_${timestamp}.xlsx`;
                ExportUtils.exportMultipleLeadsToExcel(contacts, filename);

                // Show success notification
                showNotification(
                    selectedCount > 0
                        ? `Successfully exported ${selectedCount} contacts to Excel`
                        : `Successfully exported ${contacts.length} contacts to Excel`,
                    'success'
                );
            } catch (error) {
                // Show error notification
                showNotification('Failed to export contacts to Excel. Please try again.', 'error');
            }
        } else {
            // Show warning notification
            showNotification('No contacts available to export', 'warning');
        }
    }, [contacts, selectedCount, showNotification]);

    // Handle refresh with notification
    const handleRefreshWithNotification = useCallback(() => {
        showNotification('Refreshing contacts...', 'info');
        onRefresh();
    }, [onRefresh, showNotification]);

    // Create export button text
    const exportButtonText = selectedCount > 0
        ? `Export (${selectedCount})`
        : 'Export';

    // Date display text
    const dateRangeText = dateRange[0] && dateRange[1]
        ? `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`
        : 'All time';

    // Generate weekday headers
    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3
                }}
            >
                <Typography variant="h6" component="h1">
                    Contacts ({totalContacts})
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}
                >
                    <WhiteButtonWithIcon
                        icon={RefreshIcon}
                        text="Refresh"
                        onClick={handleRefreshWithNotification}
                    />

                    <WhiteButtonWithIcon
                        ref={dateButtonRef}
                        icon={CalendarTodayOutlinedIcon}
                        text={dateRangeText}
                        onClick={handleDateRangeClick}
                        sx={{
                            '& .MuiButton-startIcon': {
                                marginRight: '8px'
                            }
                        }}
                    />

                    <WhiteButtonWithIcon
                        icon={AddIcon}
                        text="Add Leads"
                        onClick={onAddLeads}
                    />

                    <Box ref={exportButtonRef}>
                        <DarkButtonWithIcon
                            text={exportButtonText}
                            icon={
                                <OpenInNewIcon
                                    sx={{
                                        fontSize: '20px',
                                        color: '#fff',
                                        transform: 'scaleX(-1)'
                                    }}
                                />
                            }
                            onClick={handleExportClick}
                            sx={{
                                fontWeight: 700
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Popover
                open={datePopoverOpen}
                anchorEl={dateButtonRef.current}
                onClose={handleDatePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        width: 560,
                        borderRadius: '16px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        padding: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                        Select Date Range
                    </Typography>
                    <IconButton size="small" onClick={handleDatePopoverClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex' }}>
                    <Box sx={{
                        width: '160px',
                        borderRight: '1px solid #e0e0e0',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                            Preset Ranges
                        </Typography>

                        {predefinedRanges.map((preset, index) => (
                            <Button
                                key={index}
                                onClick={() => handleSelectPredefinedRange(preset.range)}
                                variant="text"
                                size="small"
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    color:
                                        (dateRange[0] && dateRange[1] &&
                                            isSameDay(dateRange[0], preset.range[0]) &&
                                            isSameDay(dateRange[1], preset.range[1])) ? '#000' : '#666',
                                    fontWeight:
                                        (dateRange[0] && dateRange[1] &&
                                            isSameDay(dateRange[0], preset.range[0]) &&
                                            isSameDay(dateRange[1], preset.range[1])) ? 600 : 400,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.05)'
                                    },
                                    padding: '4px 8px',
                                    minHeight: '32px',
                                    fontSize: '13px'
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Box>

                    <Box sx={{ flex: 1, padding: '0' }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <IconButton size="small" onClick={handlePrevMonth}>
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {formatMonthYear(calendarViewDate)}
                            </Typography>
                            <IconButton size="small" onClick={handleNextMonth}>
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{ padding: '16px' }}>
                            <Grid container>
                                {weekDays.map((day, index) => (
                                    <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                                            {day}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>

                            <Grid container sx={{ mt: 1 }}>
                                {calendarDays.map((dayObj, index) => (
                                    <Grid item xs={12 / 7} key={index}>
                                        {dayObj.day !== null && (
                                            <Box
                                                onClick={() => handleDateSelect(dayObj.date)}
                                                sx={{
                                                    height: '40px',
                                                    width: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    borderRadius: '50%',
                                                    margin: '4px auto',

                                                    // Highlight start and end dates
                                                    ...(tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    ...(tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    // Highlight range
                                                    ...(tempDateRange[0] && tempDateRange[1] &&
                                                        isInRange(dayObj.date, tempDateRange[0], tempDateRange[1]) && {
                                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                                    }),

                                                    // Highlight today
                                                    ...(isSameDay(dayObj.date, new Date()) && !isSameDay(dayObj.date, tempDateRange[0]) &&
                                                        !isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        border: '1px solid #000',
                                                    }),

                                                    '&:hover': {
                                                        backgroundColor:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#000' : 'rgba(0,0,0,0.2)',
                                                        color:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#fff' : 'inherit',
                                                    },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px' }}>
                                                    {dayObj.day}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                Start Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[0] ? formatFullDate(tempDateRange[0]) : 'Select date'}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                End Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[1] ? formatFullDate(tempDateRange[1]) : 'Select date'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                            onClick={handleResetDateRange}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: '#e0e0e0',
                                color: '#666',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    borderColor: '#c0c0c0',
                                    backgroundColor: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            Reset to All Time
                        </Button>

                        <Button
                            onClick={handleApplyDateRange}
                            variant="contained"
                            size="small"
                            disabled={!tempDateRange[0] || !tempDateRange[1]}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#a0a0a0'
                                }
                            }}
                        >
                            Apply Filter
                        </Button>
                    </Box>
                </Box>
            </Popover>

            <ExportModal
                isOpen={isExportModalOpen}
                onClose={handleExportModalClose}
                anchorEl={exportButtonRef.current}
                onExport={onExport}
                onExportCSV={handleExportToCSV}
                onExportExcel={handleExportToExcel}
                contacts={contacts}
                selectedCount={selectedCount}
            />

            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
                duration={5000}
            />

            <UpgradeSnackBar
                open={showUpgradeSnackbar}
                handleClose={handleClose}
                handleSubscribe={handleSubscribe}
                message="Upgrade to Pro or Pro Plus to edit date range"
            />
        </>
    );
};

ContactsMenu.propTypes = {
    totalContacts: PropTypes.number.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onAddLeads: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    contacts: PropTypes.array,
    selectedCount: PropTypes.number,
    onDateRangeChange: PropTypes.func
};

export default ContactsMenu;